import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ComponentType } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { formatCurrency } from "../../utils/utils"
import styles from "./UnbrokenPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Unbroken" — an EXPIRATION / renewal paywall. Built on a single behavioural
// spine: STREAK + ENDOWED-PROGRESS + honest LOSS-AVERSION, resolved with a
// CONTRAST-FRAMED plan choice. The whole screen is a warm CALENDAR: behind
// "today" runs an unbroken streak of days on which the app quietly backed up,
// encrypted and guarded every new photo. Ahead of today lie the twelve months
// of the year to come, dim and uncommitted. Choosing a plan floods those months
// with light in a warm wave — and here is where the yearly recommendation is
// *shown, not just badged*: Yearly lights the entire year (all 12 months);
// Monthly lights a single month. The reader sees, literally, how much more of
// the year one decision covers.
//
// Nothing is lifted from another paywall. The layout (a month-block calendar
// heatmap, no hero image + bullet list + card), the mechanic (a plan toggle
// that floods the year ahead with light and recedes it), the light warm-paper
// art direction, the register (steady, earned pride — not the cold urgency of
// the one prior expiration concept), and the CTA framing (keep the run going /
// renewal, not "unlock" or "start") are all new. Only the house fonts, colour
// tokens and existing feature icons are reused.
//
// Yearly is the hero: the largest price, per-month broken out, the savings, the
// default selection, and it is the plan that lights the whole calendar. Monthly
// is small and secondary. Copy is App-Store compliant: it never implies photos
// are lost when a subscription lapses. Photos never expire; lapsing simply stops
// *new* days from being added to the protected run until the user renews.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const mem = (n: number) => `${PUB}/unbroken/mem${n}.jpg`

// The protected streak so far. The native app injects the real figure; on debug
// data this stands in as the concept's anchor number (it counts up on load).
const STREAK_DAYS = 214

// The four real Premium protections that keep running for every new day. This is
// the one deliberately-conventional element on the screen (a short icon row),
// kept for instant brand clarity — this is unmistakably a private photo vault.
type Protection = {
  key: string
  Icon: ComponentType<{ className?: string }>
  label: string
}
const PROTECTIONS: Protection[] = [
  { key: "backup", Icon: svg.featureIcons.CloudVault, label: "Daily backup" },
  { key: "breakin", Icon: svg.featureIcons.Breakin, label: "Break-in alerts" },
  { key: "decoy", Icon: svg.featureIcons.Decoy, label: "Decoy vault" },
  { key: "sync", Icon: svg.featureIcons.Wifi, label: "Sync everywhere" },
]

// The twelve months of the year ahead. Six carry a real memory photo (so the
// calendar is visibly full of the user's life, not abstract dots); the other
// six are plain day-dot blocks. They alternate for a clean checker rhythm in the
// 3-column grid. `photo` blooms from grey to colour the moment its month lights.
type MonthCell = { photo?: number }
const MONTHS: MonthCell[] = [
  { photo: 1 },
  {},
  { photo: 2 },
  {},
  { photo: 3 },
  {},
  { photo: 4 },
  {},
  { photo: 5 },
  {},
  { photo: 6 },
  {},
]

// A plain month renders a small heatmap of day-dots (stylised, uniform — this is
// a warm keepsake calendar, not a functional date picker).
const DOTS_PER_MONTH = 24

// Short month labels for the year ahead, starting from the current month, so the
// "covered through" readout feels live and personal.
const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

function UnbrokenPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  // Full-bleed fixed root — respect the device safe areas so the home indicator
  // never sits over the CTA. initialData is absent on plain debug renders.
  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""

  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  // Live month labels for the year ahead (this month + 11).
  const monthLabels = useMemo(() => {
    const now = new Date()
    const base = now.getMonth()
    return MONTHS.map((_, i) => MONTH_ABBR[(base + i) % 12])
  }, [])
  const coverEndYearly = useMemo(() => {
    const now = new Date()
    const d = new Date(now.getFullYear(), now.getMonth() + 12, 0)
    return `${MONTH_ABBR[d.getMonth()]} ${d.getFullYear()}`
  }, [])
  const coverEndMonthly = useMemo(() => {
    const now = new Date()
    const d = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return `${MONTH_ABBR[d.getMonth()]} ${d.getFullYear()}`
  }, [])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  // How many of the 12 months ahead are currently lit. Animates on load (the
  // teaching demo) and whenever the plan changes.
  const [lit, setLit] = useState(0)
  const [streak, setStreak] = useState(reduce ? STREAK_DAYS : 0)
  const [showClose, setShowClose] = useState(false)

  const litAnim = useRef<number | undefined>(undefined)
  const demoRef = useRef(false)

  // Ease `lit` toward a target month count, month by month, so light floods in
  // (or recedes) as a wave rather than snapping.
  const animateLitTo = useCallback(
    (target: number, stepMs: number) => {
      if (litAnim.current) window.clearInterval(litAnim.current)
      if (reduce) {
        setLit(target)
        return
      }
      litAnim.current = window.setInterval(() => {
        setLit((cur) => {
          if (cur === target) {
            if (litAnim.current) window.clearInterval(litAnim.current)
            return cur
          }
          return cur + (cur < target ? 1 : -1)
        })
      }, stepMs)
    },
    [reduce],
  )

  // On load: count the streak up, reveal the close, and run one flood demo that
  // settles on the default (yearly = full year lit).
  useEffect(() => {
    const t = window.setTimeout(() => setShowClose(true), reduce ? 0 : 1100)

    if (!reduce) {
      // streak count-up
      const dur = 1200
      let raf = 0
      let start: number | null = null
      const tick = (now: number) => {
        if (start == null) start = now
        const k = Math.min(1, (now - start) / dur)
        const eased = 1 - Math.pow(1 - k, 3)
        setStreak(Math.round(eased * STREAK_DAYS))
        if (k < 1) raf = requestAnimationFrame(tick)
      }
      raf = requestAnimationFrame(tick)

      // teaching flood, kicked off after a short beat
      const d = window.setTimeout(() => {
        demoRef.current = true
        animateLitTo(12, 90)
      }, 520)

      return () => {
        window.clearTimeout(t)
        window.clearTimeout(d)
        cancelAnimationFrame(raf)
        if (litAnim.current) window.clearInterval(litAnim.current)
      }
    }

    // reduced motion: land on the default state immediately
    setLit(12)
    return () => window.clearTimeout(t)
  }, [reduce, animateLitTo])

  const choose = useCallback(
    (next: "yearly" | "monthly") => {
      setPlan(next)
      demoRef.current = true
      animateLitTo(next === "yearly" ? 12 : 1, next === "yearly" ? 70 : 110)
    },
    [animateLitTo],
  )

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    if (product) native.subscribe(product)
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  const coveredThrough = plan === "yearly" ? coverEndYearly : coverEndMonthly
  const ctaSub =
    plan === "yearly"
      ? `${yearlyPrice}/year — just ${perMonth}/mo. Renews yearly, cancel anytime.`
      : `${monthlyPrice}/month. Renews monthly, cancel anytime.`

  return (
    <div className={styles.root}>
      {/* warm paper wash */}
      <div className={styles.paper} aria-hidden>
        <span className={styles.warmGlow} />
        <span className={styles.grain} />
      </div>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 8, paddingBottom: bottomInset + 14 }}
      >
        {/* Top bar */}
        <div className={styles.topbar}>
          <span className={styles.wordmark}>
            <span className={styles.wmMark} aria-hidden>
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M8 10V7a4 4 0 0 1 8 0v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <rect
                  x="5.5"
                  y="10"
                  width="13"
                  height="9.5"
                  rx="2.4"
                  fill="currentColor"
                />
              </svg>
            </span>
            PRIVATE&nbsp;PHOTO&nbsp;VAULT
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Headline + streak */}
        <header className={styles.hero}>
          <p className={styles.eyebrow}>
            <span className={styles.eyebrowDot} />
            Your Premium is up for renewal
          </p>
          <h1 className={styles.title}>
            Keep the run
            <br />
            <span className={styles.titleGrad}>unbroken.</span>
          </h1>
          <p className={styles.streakLine}>
            <span className={styles.streakNum}>{streak}</span>
            <span className={styles.streakUnit}>
              days protected
              <br />
              in a row
            </span>
          </p>
          <p className={styles.sub}>
            Every day, your new photos were quietly backed up, encrypted and
            guarded — without you lifting a finger. Renew to keep every day
            ahead covered too.
          </p>
        </header>

        {/* The streak tail → today */}
        <div className={styles.tail} aria-hidden>
          <span className={styles.tailLabel}>the run so far</span>
          <div className={styles.tailTrack}>
            {Array.from({ length: 16 }).map((_, i) => (
              <span
                key={i}
                className={styles.tailCell}
                style={{ ["--td" as any]: `${i * 32}ms` }}
              />
            ))}
            <span className={styles.today}>
              <span className={styles.todayDot} />
              TODAY
            </span>
          </div>
        </div>

        {/* ── The year ahead: the calendar the plan lights up ─────────────── */}
        <section className={styles.calendarWrap} aria-label="The year ahead">
          <div className={styles.calHead}>
            <span className={styles.calTitle}>The year ahead</span>
            <span className={styles.calRead}>
              covered through <b>{coveredThrough}</b>
            </span>
          </div>

          <div className={styles.calendar}>
            {MONTHS.map((m, i) => {
              const on = i < lit
              return (
                <div
                  key={i}
                  className={`${styles.month} ${on ? styles.monthOn : ""} ${
                    m.photo ? styles.monthPhoto : styles.monthDots
                  }`}
                  style={{ ["--mi" as any]: `${Math.min(i, lit) * 55}ms` }}
                >
                  <span className={styles.monthLabel}>{monthLabels[i]}</span>

                  {m.photo ? (
                    <span className={styles.photoWrap}>
                      <img
                        src={mem(m.photo)}
                        alt=""
                        loading="eager"
                        draggable={false}
                      />
                      <span className={styles.photoKept} aria-hidden>
                        <svg viewBox="0 0 24 24" width="12" height="12">
                          <path
                            d="M5 12.5l4.4 4.4L19 7"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </span>
                  ) : (
                    <span className={styles.dots}>
                      {Array.from({ length: DOTS_PER_MONTH }).map((_, di) => (
                        <span
                          key={di}
                          className={styles.dot}
                          style={{ ["--di" as any]: `${di * 9}ms` }}
                        />
                      ))}
                    </span>
                  )}
                </div>
              )
            })}
          </div>

          {/* What keeps running, every one of those days */}
          <ul className={styles.protections} aria-label="What stays on">
            {PROTECTIONS.map((p) => (
              <li key={p.key} className={styles.protection}>
                <span className={styles.protIcon}>
                  <p.Icon />
                </span>
                {p.label}
              </li>
            ))}
          </ul>
        </section>

        {/* ── Plans — yearly is the hero, and lights the whole year ───────── */}
        <section className={styles.plans} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => choose("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>
                Best value · Save {savings}%
              </span>
            )}
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planText}>
                <span className={styles.planName}>Yearly</span>
                <span className={styles.planCovers}>
                  lights the whole year ahead
                </span>
              </span>
              <span className={styles.planPrice}>
                <span className={styles.priceBig}>{yearlyPrice}</span>
                <span className={styles.pricePer}>/yr</span>
                <span className={styles.pricePm}>{perMonth}/mo</span>
              </span>
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => choose("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planText}>
                <span className={styles.planNameSm}>Monthly</span>
                <span className={styles.planCoversSm}>one month at a time</span>
              </span>
              <span className={styles.planPriceSm}>
                <span className={styles.priceSm}>{monthlyPrice}</span>
                <span className={styles.pricePerSm}>/mo</span>
              </span>
            </span>
          </button>
        </section>

        {/* CTA */}
        <div className={styles.ctaDock}>
          <button className={styles.cta} onClick={purchase}>
            <span className={styles.ctaLabel}>Keep the run going</span>
            <svg
              className={styles.ctaArrow}
              viewBox="0 0 24 24"
              width="19"
              height="19"
              aria-hidden
            >
              <path
                d="M5 12h13M12 5l7 7-7 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Your photos never expire. If your plan lapses, your whole library
            stays safe on your device — you simply stop adding new protected
            days until you renew.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UnbrokenPaywall
